import styled from 'styled-components';

import { IconCheck } from '../../common/icons/cdl/Check';
import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';
import { Text } from '../Text/Text';

import { MainStepMarker } from './components/MainStepMarker';
import { ProgressStepsList } from './components/ProgressStepsList';
import { StepButton } from './components/StepButton';
import { ProgressStepStateEnum } from './ProgressStepState';
import type { ProgressStepState } from './ProgressStepState';

export interface ProgressSubstep<SubstepId extends string = string> {
    id: SubstepId;
    label: string;
}

export interface ProgressStep<StepId extends string = string, SubstepId extends string = string> {
    id: StepId;
    label: string;
    completed?: boolean;
    disabled?: boolean;
    substeps: readonly [ProgressSubstep<SubstepId>, ...ProgressSubstep<SubstepId>[]];
}

export interface ProgressStepSelection<StepId extends string = string, SubstepId extends string = string> {
    stepId: StepId;
    substepId: SubstepId;
}

const SubstepsList = styled.ul`
    display: flex;
    min-height: 0;
    flex-direction: column;
    gap: ${theme.space[12]};
    margin: ${theme.space[12]} 0 0;
    padding: 0;
    list-style: none;
`;

const SubstepsCollapse = styled.div`
    display: grid;
    grid-template-rows: 0fr;
    visibility: hidden;
    opacity: 0;
    transform: translateY(-4px);
    transition:
        grid-template-rows 140ms cubic-bezier(0.77, 0, 0.175, 1),
        opacity 100ms ease,
        transform 140ms cubic-bezier(0.77, 0, 0.175, 1),
        visibility 0ms linear 140ms;

    &[data-expanded='true'] {
        grid-template-rows: 1fr;
        visibility: visible;
        opacity: 1;
        transform: translateY(0);
        transition:
            grid-template-rows 180ms cubic-bezier(0.23, 1, 0.32, 1),
            opacity 120ms ease,
            transform 180ms cubic-bezier(0.23, 1, 0.32, 1),
            visibility 0ms;
    }

    @media (prefers-reduced-motion: reduce) {
        transform: none;
        transition:
            opacity 100ms ease,
            visibility 0ms linear 100ms;

        &[data-expanded='true'] {
            transition:
                opacity 100ms ease,
                visibility 0ms;
        }
    }
`;

const SubstepButton = styled(StepButton)`
    color: ${theme.colors.foreground.subtle};
    transition: color 120ms ease;

    &[data-state='${ProgressStepStateEnum.ACTIVE}'] {
        color: ${theme.colors.accent.foreground};
    }

    @media (hover: hover) and (pointer: fine) {
        &:hover:not(:disabled) {
            color: ${theme.colors.foreground.default};
        }
    }
`;

const SubstepMarker = styled.span`
    display: flex;
    flex: 0 0 ${theme.sizes[24]};
    align-items: center;
    justify-content: center;
    width: ${theme.sizes[24]};
    height: ${theme.sizes[24]};
    transition: transform 120ms cubic-bezier(0.23, 1, 0.32, 1);

    ${SubstepButton}:active:not(:disabled) & {
        transform: scale(0.88);
    }

    &::after {
        width: ${theme.sizes[8]};
        height: ${theme.sizes[8]};
        border-radius: 50%;
        background-color: currentColor;
        content: '';
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;

        ${SubstepButton}:active:not(:disabled) & {
            transform: none;
        }
    }
`;

interface ProgressStepsProps<StepId extends string = string, SubstepId extends string = string> {
    steps: readonly ProgressStep<StepId, SubstepId>[];
    selection: ProgressStepSelection<StepId, SubstepId>;
    onSelect: (selection: ProgressStepSelection<StepId, SubstepId>) => void;
}

export const ProgressSteps = <StepId extends string = string, SubstepId extends string = string>({
    steps,
    selection,
    onSelect,
}: ProgressStepsProps<StepId, SubstepId>) => {
    return (
        <ProgressStepsList>
            {steps.map((step, stepIndex) => {
                const isActive = step.id === selection.stepId;
                let state: ProgressStepState = ProgressStepStateEnum.IDLE;
                if (isActive) {
                    state = ProgressStepStateEnum.ACTIVE;
                } else if (step.disabled) {
                    state = ProgressStepStateEnum.DISABLED;
                } else if (step.completed) {
                    state = ProgressStepStateEnum.DONE;
                }
                const isMainStepAvailable =
                    state === ProgressStepStateEnum.IDLE || state === ProgressStepStateEnum.DONE;

                return (
                    <li key={step.id}>
                        <StepButton
                            type="button"
                            disabled={!isMainStepAvailable}
                            data-state={state}
                            onClick={() => onSelect({ stepId: step.id, substepId: step.substeps[0].id })}
                        >
                            <MainStepMarker>
                                {state === ProgressStepStateEnum.DONE ? (
                                    <IconCheck width={theme.sizes[16]} height={theme.sizes[16]} aria-hidden />
                                ) : (
                                    <Text variant="small" fontWeight={600}>
                                        {stepIndex + 1}
                                    </Text>
                                )}
                            </MainStepMarker>
                            <Text
                                variant="body"
                                fontWeight={600}
                                color={
                                    state === ProgressStepStateEnum.DISABLED
                                        ? 'foreground.disabled'
                                        : 'foreground.default'
                                }
                            >
                                {step.label}
                            </Text>
                        </StepButton>

                        <SubstepsCollapse data-expanded={isActive} aria-hidden={!isActive} inert={!isActive}>
                            <Box minHeight={0} overflow="hidden">
                                <SubstepsList>
                                    {step.substeps.map((substep) => {
                                        const isSubstepActive = substep.id === selection.substepId;

                                        return (
                                            <li key={substep.id}>
                                                <SubstepButton
                                                    type="button"
                                                    disabled={isSubstepActive}
                                                    data-state={
                                                        isSubstepActive
                                                            ? ProgressStepStateEnum.ACTIVE
                                                            : ProgressStepStateEnum.IDLE
                                                    }
                                                    onClick={() =>
                                                        onSelect({
                                                            stepId: step.id,
                                                            substepId: substep.id,
                                                        })
                                                    }
                                                >
                                                    <SubstepMarker />
                                                    <Text variant="small" fontWeight={isSubstepActive ? 600 : 400}>
                                                        {substep.label}
                                                    </Text>
                                                </SubstepButton>
                                            </li>
                                        );
                                    })}
                                </SubstepsList>
                            </Box>
                        </SubstepsCollapse>
                    </li>
                );
            })}
        </ProgressStepsList>
    );
};
